import React, { useCallback, useMemo } from 'react';
import FormField from '../../components/common/FormField';

type FormFieldType = 'number' | 'text' | 'select' | 'date' | 'file' | 'checkbox' | 'hidden';

export interface GenericFieldConfig {
  label: string;
  name: string;
  type: FormFieldType;
  options?: Array<{ key: string | number; value: string }>;
  placeholderOptionLabel?: string;
  singleField?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  showNumberAsFixed2?: boolean;
  rowFullFlex?: boolean;
  multiple?: boolean;
  [key: string]: any;
}

interface SectionConfig {
  title?: string;
  fields: GenericFieldConfig[];
}

interface GenericFormTabProps {
  fieldConfigs: Array<GenericFieldConfig> | Array<SectionConfig>;
  formData: any;
  setFormData: (data: any) => void;
  externalValidationErrors?: { [fieldName: string]: string };
  className?: string;
  isCheckboxRight?: boolean;
}

const isSectioned = (configs: any[]): configs is SectionConfig[] => {
  return configs.length > 0 && Array.isArray(configs[0].fields);
};

const GenericFormTab: React.FC<GenericFormTabProps> = ({
  fieldConfigs,
  formData,
  setFormData,
  externalValidationErrors = {},
  className = '',
  isCheckboxRight,
}) => {
  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      setFormData({ ...formData, [field]: value });
    },
    [formData, setFormData],
  );

  const renderFormField = useMemo(
    () => (field: GenericFieldConfig) => {
      if (field.type === 'hidden') {
        return <div key={field.name} style={{ flex: 1 }} />;
      }
      const value = formData[field.name];
      let displayValue = value;
      const handleBlur = () => {
        if (field.showNumberAsFixed2 && field.type === 'number') {
          let num = typeof value === 'string' ? parseFloat(value) : value;
          if (typeof num === 'number' && !isNaN(num) && value !== '' && value !== null) {
            num = parseFloat(num.toFixed(2));
            handleFieldChange(field.name, num);
          }
        }
      };
      if (field.showNumberAsFixed2 && field.type === 'number' && typeof value === 'number') {
        displayValue = value.toFixed(2);
      }
      return (
        <FormField
          key={field.name}
          label={field.label}
          name={field.name}
          type={field.type}
          value={displayValue}
          onChange={(_, v) => handleFieldChange(field.name, v)}
          onBlur={handleBlur}
          required={field.required}
          placeholderOptionLabel={field.placeholderOptionLabel}
          options={field.options?.map((opt) => ({ ...opt, value: String(opt.value) }))}
          error={externalValidationErrors?.[field.name]}
          isCheckboxRight={isCheckboxRight}
          disabled={field.disabled}
          readOnly={field.readOnly}
          multiple={field.multiple}
        />
      );
    },
    [formData, handleFieldChange, externalValidationErrors, isCheckboxRight],
  );

  const renderFormFields = useCallback(
    (fields: GenericFieldConfig[], sectionTitle?: string) => {
      const rows = [];
      for (let i = 0; i < fields.length; ) {
        const field = fields[i];
        if (field.rowFullFlex) {
          rows.push(
            <div key={`row-${i}`} style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>{renderFormField(field)}</div>
            </div>,
          );
          i += 1;
        } else if (field.singleField) {
          rows.push(
            <div key={`row-${i}`} style={{ display: 'flex', gap: 16 }}>
              {renderFormField(field)}
              <div style={{ flex: 1 }} />
            </div>,
          );
          i += 1;
        } else {
          const nextField = fields[i + 1];
          if (nextField && !nextField.singleField && !nextField.rowFullFlex) {
            rows.push(
              <div key={`row-${i}`} style={{ display: 'flex', gap: 16 }}>
                {renderFormField(field)}
                {renderFormField(nextField)}
              </div>,
            );
            i += 2;
          } else {
            rows.push(
              <div key={`row-${i}`} style={{ display: 'flex', gap: 16 }}>
                {renderFormField(field)}
                <div style={{ flex: 1 }} />
              </div>,
            );
            i += 1;
          }
        }
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {sectionTitle && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid var(--border-color)',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ margin: 0 }}>{sectionTitle}</h3>
            </div>
          )}
          {rows}
        </div>
      );
    },
    [renderFormField],
  );

  return (
    <div className={className}>
      {isSectioned(fieldConfigs)
        ? fieldConfigs.map((section, idx) => (
            <div key={section.title || idx}>{renderFormFields(section.fields, section.title)}</div>
          ))
        : renderFormFields(fieldConfigs)}
    </div>
  );
};

export default GenericFormTab;
